import { NIGHTLIFE_EVENTBRITE_CATEGORY } from '@eventbrite/categories';
import { logEvent } from '@eventbrite/statsig';
import loadable from '@loadable/component';
import { useQueryClient } from '@tanstack/react-query';
import React, { ReactNode } from 'react';
import { StickyContainer } from 'react-sticky';
import { SeasonalRibbon } from '../../../components/SeasonalRibbon';
import { LazyRenderer } from '../components/LazyRenderer';
import { STATSIG_CATEGORYBROWSE_PAGE_VIEW } from '../constants/analytics';
import { CUSTOM_CATEGORY_HEADER } from '../constants/categoryCustomConfig';
import { CategoryBrowseBreadCrumb } from '../containers/CategoryBrowseBreadCrumb';
import { CategoryBrowseDateFilter } from '../containers/CategoryBrowseDateFilter';
import { CategoryBrowseLocationAutocomplete } from '../containers/CategoryBrowseLocationAutocomplete';
import { useEnvContext, useRouteContext } from '../context';
import { useLocation } from '../hooks/queries/location';
import { CarouselSequence } from '../utils/CarouselSequence';
import { getLanguageSubdirectorySubPath } from '../utils/localeSubPath';
import './CategoryBrowsePage.scss';
import './NightlifePage.scss';

const CategoryBrowseThemeHeaderContainer = loadable(
    () => import('../containers/CategoryBrowseThemeHeaderContainer'),
    {
        resolveComponent: (components: {
            CategoryBrowseThemeHeaderContainer: React.ReactElement;
        }) => components.CategoryBrowseThemeHeaderContainer,
    },
);

const CategoryBrowseBackground = loadable(
    () => import('../components/CategoryBrowseBackground'),
    {
        resolveComponent: (components: {
            CategoryBrowseBackground: React.ReactElement;
        }) => components.CategoryBrowseBackground,
    },
);
// CategoryBrowse theme page can be used for existing categories,subcategories and tag(ex: nightlife) pages
//TODO: Set date filter to leverage current future and date when both applied
export const CategoryBrowseThemePage: React.FunctionComponent = () => {
    // Env and General Data
    const envContext: any = useEnvContext();
    const isMobile = envContext?.env?.isMobile;
    const user = envContext?.user;
    const locale = envContext?.env?.localeInfo?.locale || 'en_US';
    const isBotRequest = envContext?.isBotRequest;
    const tld = envContext?.env?.localeInfo.tld;

    //Current Tag and Search Values
    const { parsedValues, location, featureFlags } = useRouteContext();
    const activeTag = parsedValues?.activeTag || '';
    const date = parsedValues?.date;
    const customDate = parsedValues?.customDate;
    const queryClient = useQueryClient();

    const isNightlifePage = activeTag === NIGHTLIFE_EVENTBRITE_CATEGORY;
    const mainClassName = isNightlifePage
        ? 'nightlife__main'
        : 'category-browse__main';

    React.useEffect(() => {
        if (activeTag) {
            logEvent(STATSIG_CATEGORYBROWSE_PAGE_VIEW);
        }
    }, [activeTag]);

    React.useEffect(() => {
        queryClient.clear();
    }, [queryClient]);

    const { data: locationData } = useLocation();
    const placeId = locationData?.placeId || '';
    const currentPlace = locationData?.currentPlace || '';

    const HeaderContainer = () => {
        return (
            <CategoryBrowseThemeHeaderContainer
                activeCategory={activeTag}
                isAuthenticated={!!user?.isAuthenticated}
                locale={locale}
            />
        );
    };

    const defaultSequence = CarouselSequence({
        activeCategory: activeTag,
        date: date,
        customDate: customDate,
        location: location,
        locationData: locationData,
        locale: locale,
        user: user,
        isMobile: isMobile,
        isBotRequest: isBotRequest || false,
        placeId: placeId,
        currentPlace: currentPlace,
        tld: tld || '',
        enableSeasonalModule: featureFlags?.enableSeasonalModule || false,
        enableLighthouseCreatorsModule:
            featureFlags?.enableLighthouseCreatorsModule || false,
    });

    const renderEventCarousels = () => {
        return (
            <div className="event_carousel">
                {defaultSequence.map((ComponentData, index) => {
                    const {
                        component: Component,
                        params,
                        renderCondition,
                    } = ComponentData;
                    if (!Component) {
                        return null;
                    }
                    const shouldRender =
                        renderCondition !== undefined ? renderCondition : true;
                    return (
                        shouldRender && <Component key={index} {...params} />
                    );
                })}
            </div>
        );
    };

    let StickyWrapper = ({ children }: { children: ReactNode }) => (
        <> {children} </>
    );

    if (isNightlifePage) {
        StickyWrapper = ({ children }: { children: ReactNode }) => (
            <StickyContainer> {children} </StickyContainer>
        );
    }

    const BodyContainer = () => {
        return (
            <main className={mainClassName}>
                <StickyWrapper>
                    <LazyRenderer numChildInitialRender={isBotRequest ? -1 : 2}>
                        <section className="category-browse__filters">
                            {!CUSTOM_CATEGORY_HEADER[activeTag]
                                ?.showLocationPill && (
                                <CategoryBrowseLocationAutocomplete />
                            )}
                            {CUSTOM_CATEGORY_HEADER[activeTag]
                                ?.showDateFilter != false && (
                                <CategoryBrowseDateFilter locale={locale} />
                            )}
                        </section>
                        {renderEventCarousels()}
                    </LazyRenderer>
                </StickyWrapper>
            </main>
        );
    };

    return (
        <CategoryBrowseBackground activeCategory={activeTag}>
            <StickyContainer style={{ overflowY: 'auto' }}>
                <SeasonalRibbon
                    season="holidays"
                    locationSlug={
                        envContext?.request?.default_location_for_tld
                            ?.location_slug || 'online'
                    }
                    country={locationData?.country}
                    browseSurface={'category-browse'}
                    isMobile={isMobile}
                />
                <CategoryBrowseBreadCrumb
                    languageSubdirectory={getLanguageSubdirectorySubPath(
                        envContext,
                    )}
                />
                {HeaderContainer()}
                {BodyContainer()}
            </StickyContainer>
        </CategoryBrowseBackground>
    );
};

export default CategoryBrowseThemePage;
